import * as THREE from "three";
import { action, computed, makeObservable, observable } from "mobx";
import { v4 as uuidv4 } from "uuid";
import helpers from "../helpers";
import { reservedUuids,  InstructionState, InstructionStateStatus  } from "./constants";
import { Notification } from "./Notification";


const ArrayMatrix_Identity = Object.freeze(new THREE.Matrix4().toArray());

export function Instruction(pApp, pSequence, pInstructionSet, pInstData) {
  Notification.call(this);

  this.mApp = pApp;
  this.mSequence = pSequence;
  this.mInstructionSet = pInstructionSet;
  this.mCategory = '';
  this.mName = '';
  /** @type InstructionState */
  this.mState = {
    status: InstructionStateStatus.INACTIVE,
    subInstructionState: {}
  };
  this.mParameters = {};
  this.mUuid = uuidv4();

  // Initialize instruction
  this.fromJson(pInstData);

  makeObservable(this, {
    mUuid: observable,
    mName: observable,
    mState: observable,
    mParameters: observable,
    sequence: computed,
    name: computed,
    state: computed,
    uuid: computed,
    parameters: computed,
    category: computed,
    setName: action,
    setState: action,
    updateParams: action,
    setParameter: action,
    fromJson: action,
  });
}

Instruction.prototype = Object.create(Notification.prototype);
Instruction.prototype.constructor = Instruction;

Instruction.prototype.setDirty = function() {
  this.mApp.setDirty();
};

Instruction.prototype.getCategory = function() { return this.mCategory; };

Instruction.prototype.inferCategory = function() {
  const _lThis = this;
  const _lCategory = Object.keys(this.mInstructionSet).find(function (pCategoryName) {
    return _lThis.mInstructionSet[pCategoryName].hasOwnProperty(_lThis.getName());
  });
  return _lCategory;
};

Instruction.prototype.getName = function() { return this.mName; };

Instruction.prototype.setName = function(pName) {
  const lThis = this;
  if (this._isValidInstruction(pName)) {
    const lConfigManager = this.mSequence.mLibrary.getConfigManager();
    // find the first axis for single/combined move
    const lAxesUUID = this.mParameters.axes ? this.mParameters.axes : [this.mParameters];
    let lAxisUUID = lAxesUUID.find(function(pUuidDistance) {
      return lConfigManager.getAxisByUuid(pUuidDistance.axis);
    });
    lAxisUUID = lAxisUUID?.axis;

    this.mName = pName;
    this.mCategory = this.inferCategory();
    this.mParameters = {};
    this._getInstructionModel().parameters.forEach(function(pParamInfo) {

      if(Array.isArray(pParamInfo.default) && pParamInfo.count) {
        // Verify that the actuator is valid, could be pneumatic, try to find the next valid one from the instructions
        if(pName.match(/Combined/i) && lConfigManager.getAxisByUuid(lAxisUUID).isNonMotorized()) {
          const otherAxes = lAxesUUID.filter( a => {
            if(!a.axis || a.axis == lAxisUUID) { return false; }
            const axis = lConfigManager.getAxisByUuid(a.axis);
            if(!axis || axis.isNonMotorized()) { return false; }
            return true;
          });
          // if not valid actuators are found, actuators from other controllers will be proposed
          if(otherAxes.length) { lAxisUUID = otherAxes[0].axis; }
        }

        lThis.mParameters[pParamInfo.name] = Array.from(helpers.deepClone(pParamInfo.default));
        // Add missing parameters from the default array
        const _lMissing = pParamInfo.count - lThis.mParameters[pParamInfo.name].length;
        for(let i = 0; i < _lMissing; ++i) {
          const subParam = {};
          for(let j = 0; j < pParamInfo.unit.length; ++j) {
            const subParamInfo = pParamInfo.unit[j];
            subParam[subParamInfo.name] = subParamInfo.default;
          }
          lThis.mParameters[pParamInfo.name].push(subParam);
        }
        // The default axis parameter value should be the first one of the config compatible with allowedAxisType ; if it exists
        pParamInfo.unit.filter(function(pSubInfo) {
          const lCompatibleAxisList = lThis._listCompatibleAxisUuids(pSubInfo, lAxisUUID);
          if (pSubInfo.unit && pSubInfo.unit == "axis" && pSubInfo.name && pParamInfo.count) {
            for (let i = 0; i < pParamInfo.count; i++) {
              if (lCompatibleAxisList.length > 0) {
                lThis.mParameters[pParamInfo.name][i][pSubInfo.name] = lCompatibleAxisList.shift();
              }
              else {
                lThis.mParameters[pParamInfo.name][i][pSubInfo.name] = pSubInfo.default; // Back to default
              }
            }
          }
        });
      }
      else {
        lThis.mParameters[pParamInfo.name] = helpers.deepClone(pParamInfo.default);
        // this is the case for single move instruction
        if(pParamInfo.unit == "axis") {
          const lCompatibleAxisList = lThis._listCompatibleAxisUuids(pParamInfo);
          if(lCompatibleAxisList.length > 0) {
            lThis.mParameters[pParamInfo.name] = lCompatibleAxisList[0];
          }
        }

        if (lThis.mCategory === 'waitFor' && pParamInfo.name === 'unpackVariableName') {
          const lVariableObject = lThis.mApp.addApplicationParameter('unpackConst');
          lThis.setParameter('unpackVariableName', lVariableObject.name);
        }
      }
    });
    this.setDirty();
    this.notify('onNameChanged', this.mName);
  } else {
    console.warn('Instruction [' + this.mCategory + '] does not support action [' + this.mName + ']');
  }
};

/**
 * @param {InstructionState|InstructionStateStatus} state
 */
Instruction.prototype.setState = function(state) {
  if (typeof state === 'string') {
    this.mState = {
      status: state,
      subInstructionState: {}
    };
  } else if (typeof state === 'object') {
    this.mState = state;
  }
};

Instruction.prototype.setActiveRobotKeyFrameID = function (keyframeID) {
  this.setState({
    status: keyframeID === undefined ? InstructionStateStatus.INACTIVE : InstructionStateStatus.RUNNING,
    subInstructionState: {
      activeRobotKeyFrameID: keyframeID
    },
  });
};

Instruction.prototype.isCombined = function() { return this.getName().match(/combined/i) != null; };

Instruction.prototype._getInstructionModel = function() {
  return this.mInstructionSet[this.getCategory()][this.getName()];
};

Instruction.prototype.listAxisParametersNames = function() {
  const _lAxisParametersNames = [] ;
  this._getInstructionModel().parameters.forEach(function(pInfo) {
    if(pInfo.unit && pInfo.unit == "axis") {
      _lAxisParametersNames.push(pInfo.name);
    }
    else if (pInfo.format && pInfo.format == "array" && pInfo.unit){
      const _lSubElementNames = [];
      pInfo.unit.filter(function(pSubInfo) {
        if(pSubInfo.unit && pSubInfo.unit == "axis" && pSubInfo.name && _lSubElementNames.indexOf(pSubInfo.name) < 0 && pInfo.count) {
          // If the name is not already part of the array
          _lSubElementNames.push(pSubInfo.name);
          for (let i = 0; i < pInfo.count; i++){
            _lAxisParametersNames.push( pInfo.name + '.' + i + '.' + pSubInfo.name );
          }
          return pSubInfo.name;
        }
      });
    }
  });
  return _lAxisParametersNames;
};

Instruction.prototype._isValidInstruction = function(pName) {
  const lThis = this;
  return Object.keys(this.mInstructionSet).reduce(function(acc, curr, i, arr) {
    acc = acc || Object.keys(lThis.mInstructionSet[curr]).includes(pName);
    if (acc) {
      arr.splice(i);
    }
    return acc;
  }, false);
};

Instruction.prototype.listParameters = function() { return Object.keys(this.mParameters); };

Instruction.prototype.getParameters = function() { return this.mParameters; };

/**
 * Creates a list of axis UUIDs that meet the parameters provided. Additionally, if pSelectedUuid
 * is provided, we make sure that the axis also belongs to the same controller.
 *
 * @param {Object} pParameter Parameters to meet.
 * @param {String} pSelectedUuid Selected UUID, if undefined list all
 * @returns {Array<String>} List of available axis UUIDs for this instruction.
 */
Instruction.prototype._listCompatibleAxisUuids = function(pParameter, pSelectedUuid) {
  const lConfigManager = this.mSequence.mLibrary.getConfigManager();
  let lSelectedAxis = pSelectedUuid ? lConfigManager.getAxisByUuid(pSelectedUuid) : undefined;
  const lCompatibleAxisList = [];

  if(!lSelectedAxis) {
    if (pParameter.allowedAxisType !== undefined) {
      return lConfigManager.listAxes()
        .filter(pAxis => pParameter.allowedAxisType.includes(pAxis.getType()))
        .map(pAxis => pAxis.mUUID);
    }
    return lConfigManager.listAxes().map(pAxis => pAxis.mUUID);
  }

  if (lConfigManager.numberOfAxes() > 0 && pParameter && pParameter.unit == 'axis' && pParameter.allowedAxisType){
    if (lSelectedAxis && !pParameter.allowedAxisType.includes(lSelectedAxis.getType())) {
      // Our currently selected axis is invalid. Lets try to find a compatible actuator from the same controller.
      const nextBestGuess = lSelectedAxis.getConfig().listAxes().find(a => pParameter.allowedAxisType.includes(a.getType()));
      if(nextBestGuess) { lSelectedAxis = nextBestGuess; }
      else { lSelectedAxis = undefined; }
    }

    lConfigManager.listAxes().forEach(function(pAxis) {
      // If the axis type is valid and the axis index is valid as well
      if (pParameter.allowedAxisType.includes(pAxis.getType())) {
        if (!lSelectedAxis || lSelectedAxis.getControllerId() === pAxis.getControllerId()) {
          lCompatibleAxisList.push(pAxis.mUUID);
        }
      }
    });

    // Always sort the currently select unique identifier into the first position.
    if (lSelectedAxis) {
      lCompatibleAxisList.sort(function(pFirst, pSecond) {
        if (pFirst === pSelectedUuid)       { return -1; }
        else if (pSecond === pSelectedUuid) { return 1; }
        else                                { return 0; }
      });
    }
  }

  return lCompatibleAxisList;
};

Instruction.prototype.getParameter = function(pName) {
  let _lParams = this.mParameters;
  const _lNames = pName.split('.');
  let _lValue;

  for(let i = 0; i < _lNames.length; ++i) {
    if(_lParams.hasOwnProperty(_lNames[i])) {
      _lValue = _lParams[_lNames[i]];
      _lParams = _lParams[_lNames[i]];
    } else {
      _lValue = undefined;
    }
  }
  return _lValue;
};

Instruction.prototype._getParameterData = function (pName){
  let _lParams = this.mParameters;
  const _lNames = pName.split('.');
  let _lName;
  let _lParamInfo;

  if(_lNames.length > 0) {
    // Find parameter info
    _lParamInfo = Object.assign({}, this.mInstructionSet[this.mCategory][this.mName].parameters.find(function(pInfo) {
      if(_lNames[0] == pInfo.name) {
        return pInfo;
      }
    }));
    if(_lParamInfo && _lParamInfo.format == "array" && _lNames.length >= 3) {
      // pName = "axes.0.position" => _lNames = ["axes", "0", "position"]. We want the "position" info at index = 2
      _lParamInfo = _lParamInfo.unit.find(function(pInfo) {
        if(_lNames[2] == pInfo.name) {
          return pInfo;
        }
      });
    }

    if (_lNames.includes('undefined')) {
      console.error(`_lNames includes an undefined parameter.`);
      _lParams = undefined;

    } else {
      // Find parameter
      for(let i = 0; i < _lNames.length; ++i) {
        _lName = _lNames[i];
        if(_lParams.hasOwnProperty(_lName)) {
          if(i != _lNames.length - 1) {
            _lParams = _lParams[_lName];
          }
        } else {
          console.error('Instruction._getParameterData: No parameter "' + pName + '" found.');
          _lParams = undefined;
          break;
        }
      }
    }
  }

  return {_lParams:_lParams, _lNames:_lNames, _lName:_lName, _lParamInfo:_lParamInfo};
};

Instruction.prototype.getRobotId = function () {
  const robotId = this.getParameter('robotId');
  if ( robotId ) {
    return robotId;
  }

  const clipId = this.getParameter('clipId');
  if ( clipId && this.mApp.robotParameters.clips?.length ) {
    const clipsFiltered = this.mApp.robotParameters.clips.filter( clip => clip.id === clipId );
    const robotId = clipsFiltered[0].robotId;
    this.mParameters.robotId = robotId;

    return robotId;
  }

  return undefined;
};

function validateInstructionItemUuid(pConfigManager, pType, pUuid){
  switch (pType) {
    case "axis":
      return pConfigManager.getAxisByUuid(pUuid) != undefined;
    case "inputUuid":
      return pConfigManager.getInputByUuid(pUuid) != undefined;
    case "outputUuid":
      return pConfigManager.getOutputByUuid(pUuid) != undefined;
    default:
      return false;
  }
}

/**
 * This function validates an instruction's parameter excluding special cases such as axes.
 * Will output errors and correct value to be within bounds if possible
 *
 * @param {*} pParameters Parameters for the given instructions
 * @param {*} pParamInfo Info on the parameter to validate
 * @param {*} pName Name of the parameter to validate. Is key in pParameters
 * @param {*} pValue Value to be set for given parameter
 */
Instruction.validateInstructionParameter = function(pParameters, pParamInfo, pName, pValue, pConfigManager){
  // skip validation on evaluatable parameters
  if (pParamInfo.acceptedVariableTypes?.includes("evaluatable")) {
    return {};
  }

  const lErrorList = {};
  switch(pParamInfo.format) {
    case 'integer':
      if (typeof pValue != 'number' || isNaN(pValue)) {
        lErrorList[pName] = 'Must be a number.';
        if (undefined != pParamInfo.default){pParameters[pName] = pParamInfo.default;}
        else {pParameters[pName] = pParamInfo.minimum || 0;}
      } else if(undefined != pParamInfo.minimum && pValue < pParamInfo.minimum) {
        lErrorList[pName] = 'Must be above minimum [' + pParamInfo.minimum + '].';
        pParameters[pName] = pParamInfo.minimum;
      } else if(undefined != pParamInfo.maximum && pValue > pParamInfo.maximum) {
        lErrorList[pName] = 'Must be below maximum [' + pParamInfo.maximum + '].';
        pParameters[pName] = pParamInfo.maximum;
      }
      break;
    case 'string':
      if(typeof pValue != 'string') {
        lErrorList[pName] = 'Must be a string.';
      }
      break;
    case 'oneOf':
      if(pName == "axis" || pName == "inputUuid" || pName == "outputUuid"){
        if(!validateInstructionItemUuid(pConfigManager, pName, pValue)){
          lErrorList[pName] = "Invalid uuid for parameter";
        }
        break;
      }
      if ('ioExpander' == pParamInfo.unit) {
        break;
      }
      if(!pParamInfo.values.includes(pValue)) {
        lErrorList[pName] = 'Must be a value from ' + JSON.stringify(pParamInfo.values) + '. And value is ' + pValue;
      }
      break;
    case 'float':
      if (isNaN(pValue)) {
        lErrorList[pName] = 'Must be a number';
      }
      break;
    case 'array':
      if (!Array.isArray(pValue)) {
        lErrorList[pName] = 'Must be an array.';
      } else if (!pValue.length) {
        lErrorList[pName] = 'Cannot be empty.';
      }
      break;
    case 'object':
      if ('object' !== typeof pValue) {
        lErrorList[pName] = 'Must be an object.';
      } else if (!Object.keys(pValue).length) {
        lErrorList[pName] = 'Cannot be empty.';
      }
      break;
    case 'uuid':
      if ('string' !== typeof pValue || !/\S{8}-\S{4}-\S{4}-\S{4}-\S{12}$/.test(pValue)) {
        lErrorList[pName] = 'Must be an uuid.';
      }
      break;
    default:
      console.log('Instruction.setParameter : No validation found for parameter format [' + pParamInfo.format + ']');
      break;
  }

  if(lErrorList[pName]) {
    console.warn('Instruction parameter set with an initial invalid application parameter (application parameter may be valid during runtime).');
  }

  return lErrorList;
};

Instruction.prototype.setParameter = function(pName, pValue) {
  const _lParameterData = this._getParameterData(pName);
  const _lParams    = _lParameterData._lParams;
  const _lNames     = _lParameterData._lNames;
  const _lName      = _lParameterData._lName;
  const _lParamInfo = _lParameterData._lParamInfo;
  const _lValueToSet = pValue;

  const _lThis = this;

  if (!_lParams && _lNames.includes('listOfEvents') && !_lNames.includes('undefined')) {
    // Creating a new event does not add an event to the mParameters, therefore we need to push a default
    // value to mParameters. Need a recursive function "setParameter" to recall the new _lParameterData.
    const default_event = Object.assign({}, this.mInstructionSet.waitFor.events.parameters.find(a => a.name == 'listOfEvents').default[0]);
    this.mParameters.listOfEvents.push(default_event);
    this.setParameter(pName, pValue);
    return;

  } else if (!_lParams) {
    return;
  }

  let _lAxisParametersNames = [];
  const _lDependantParamInfoNames = [];

  let lParameterErrors = {};
  if(_lParams && _lParamInfo) {
    switch(_lParamInfo.format) {
      case 'integer':
      case 'float':
      case 'array':
      case 'object':
      case 'uuid':
        // Validate basic parameters
        lParameterErrors = Instruction.validateInstructionParameter(this.mParameters, _lParamInfo, pName, pValue, this.mApp.mLibrary.getConfigManager());
        break;
      case 'oneOf':
        if ('axis' == _lParamInfo.unit) {
          _lAxisParametersNames = this.listAxisParametersNames();
          if(reservedUuids.NONE != pValue) {
            _lAxisParametersNames = _lAxisParametersNames.filter( _lDependantParamInfoName => _lDependantParamInfoName != pName);
          }
          // If axis has been changed and there is an axisDependant unit in the other instruction parameters
          // Find all other parameters that would have an axisDependant unit
          this.mInstructionSet[this.mCategory][this.mName].parameters.filter(function(pInfo) {
            if(pInfo.unit && pInfo.unit.includes("axisDependant")) {
              _lDependantParamInfoNames.push(pInfo.name);
              return pInfo;
            }
            else if (pInfo.format == "array"){
              const _lSubElementNames = [];
              pInfo.unit.filter(function(pSubInfo) {
                if(pSubInfo.unit && pSubInfo.unit.includes("axisDependant") && _lSubElementNames.indexOf(pSubInfo.name) < 0) {
                  // If the name is not already part of the array
                  _lSubElementNames.push(pSubInfo.name);
                  _lDependantParamInfoNames.push( pInfo.name + '.' + _lNames[1] + '.' + pSubInfo.name );
                  return pSubInfo.name;
                }
              });
            }
          });
          break;
        }

        // Validate cases that are not axes
        lParameterErrors = Instruction.validateInstructionParameter(this.mParameters, _lParamInfo, pName, pValue, this.mApp.mLibrary.getConfigManager());
        break;
      case 'string':
        // validation of strings is done at widget level through validation functions (validate expressions, required, etc)
        break;
      default:
        console.log('Instruction.setParameter : No validation found for parameter format [' + _lParamInfo.format + ']');
        break;
    }
  }

  if(lParameterErrors[pName]) {
    console.warn('Instruction parameter set with an initial invalid application parameter (application parameter may be valid during runtime).');
  }

  const lPreviousValue = _lParams[_lName];
  _lParams[_lName] = _lValueToSet;

  // dispatch some axis related notifications
  if(_lParams && _lParamInfo && _lParamInfo.format === 'oneOf' && _lParamInfo.unit == 'axis' ) {
    // Change the displayed unit of this parameter
    _lDependantParamInfoNames.forEach(function(_lDependantParamInfoName){
      _lThis.notify('onParameterUnitChanged', _lDependantParamInfoName, pValue);
    });

    // If axis has been changed and there are other axis in the other instruction parameters
    // Notify that axis dropdown enabled/disabled values should change
    _lAxisParametersNames.forEach( function (_lDependantParamInfoName) {
      _lThis.notify('onParameterAxisSelected', _lDependantParamInfoName, lPreviousValue, pValue);
    });
  }

  this.setDirty();
  this.notify('onParameterChanged', pName, _lParams[_lName]);
};


Instruction.prototype._patchDefaultParameterForPath = function(pParamPath) {
  // NOTE: in some case, the model can more possible element (array) then the params, we wil create them here
  // otherwise later on we wont be able to set them,
  // the reason we are doing them on load instead of on save
  // is that it also solves the default values needed when building the widgets
  // ex: combined moves can have 4 drives, but 3 in the parameters
  console.warn(`Parameter path does not exists for <${pParamPath}>, patching to default from the model.`, this.mParameters);

  const lModel = this._getInstructionModel();
  let lPatch = null;
  if(lModel && lModel.parameters) {
    // go trough the model and build an object of parameters with default values
    lPatch = lModel.parameters.reduce(function(pAccumulator, pCurrent) {
      if(pCurrent.unit == undefined) { return pAccumulator; }
      if(!Array.isArray(pCurrent.unit)) { return pAccumulator; }
      // for combined moves, defaults are in `parameters.unit[{name, default}]`
      pAccumulator = pCurrent.unit.reduce(function(pAccumulator, pCurrent) {
        if(pCurrent.name != undefined && pCurrent.default != undefined) { pAccumulator[pCurrent.name] = pCurrent.default; }
        return pAccumulator;
      }, pAccumulator);

      return pAccumulator;
    }, {});

    if(lPatch) { helpers.assignObjectAttribute(this.mParameters, pParamPath, reservedUuids.NONE, lPatch);  /* re-assign with a patch*/ }
  }
  if(!lPatch){
    console.error(`Config mismatch: JSON is ${JSON.stringify(this.mParameters)} while machine config: ${pParamPath}`);
    console.error('Delete the instruction and add it back, could not recover.', pParamPath);
  }
};

Instruction.prototype.fromJson = function(pJson) {
  this.mName = pJson.name;
  // Infer category name from instruction name
  this.mCategory = this.inferCategory() || pJson.category;

  // TODO move to robot app machine
  if (this.mCategory === "robot") {
    const getClipById = id => this.mApp?.robotParameters?.clips.find(c => c.id === id) || this.mSequence?.robotParameters?.clips.find(c => c.id === id);
    const clipId = pJson?.parameters?.clipId || this.mApp?.robotParameters?.clips[0]?.id || 'new-clip';
    const endEffectorToolCenterPoint = pJson?.parameters?.endEffectorToolCenterPoint || this.mApp?.mLibrary?.mConfigManager?.m3DContext?.robotUtils?.ArrayMatrix__get_endEffector_flange() || ArrayMatrix_Identity;
    const executionReferenceFrame = pJson?.parameters?.executionReferenceFrame || getClipById(clipId)?.teachingReferenceFrame;
    this.mParameters = helpers.deepClone({
      ...pJson.parameters,
      clipId,
      endEffectorToolCenterPoint,
      ...(executionReferenceFrame && { executionReferenceFrame }),
      instructionIndex: this.mSequence.mInstructions.length
    });
  } else {
    this.mParameters = helpers.deepClone(pJson.parameters);
  }

  // Assign unknown axes to axes GUIDs, or unknown if we fail to find them.
  const _lAxisParametersNames = this.listAxisParametersNames();
  const lThis = this;
  if (_lAxisParametersNames.length > 0) {
    _lAxisParametersNames.forEach(function (_lAxisParam) {
      const lAxisGuid = lThis.getParameter(_lAxisParam);

      if(lAxisGuid == undefined) {
        // NOTE: parameters are not in sync with the model, the UI assumes it is, let's patch
        // this will not update the execution-engine yet, but will make the parameters sync with the UI
        lThis._patchDefaultParameterForPath(_lAxisParam);
      }

      const lAxis = lAxisGuid ? lThis.mSequence.mLibrary.getConfigManager().getAxisByUuid(lAxisGuid) : undefined;
      if (lAxisGuid && lAxisGuid !== reservedUuids.ALL && lAxis == undefined) {
        // Show "Not Configured" if the axis does not belong to the config
        helpers.assignObjectAttribute(lThis.mParameters, _lAxisParam, reservedUuids.UNKNOWN);
      } else {
        helpers.assignObjectAttribute(lThis.mParameters, _lAxisParam, lAxisGuid);
      }
    });
  }
};

Instruction.prototype.toJson = function() {
  const lPayload = {
    category : this.mCategory,
    name : this.mName,
    uuid : this.mUUID,
    parameters : helpers.deepClone(this.mParameters)
  };

  return lPayload;
};

Instruction.prototype.updateParams = function(pKey, pParamsPayload) {
  this.mParameters[pKey] = pParamsPayload;
  this.notify('onParamUpdate');
  this.setDirty();
};

Instruction.prototype.isWaitingInstruction = function() {
  return this.mCategory === 'waitFor' && this.mName !== 'amountOfTime';
};

Object.defineProperty(
  Instruction.prototype,
  'uuid',
  {
    get: function () {
      return this.mUuid;
    }
  }
);

Object.defineProperty(
  Instruction.prototype,
  'parameters',
  {
    get: function () {
      return this.getParameters();
    }
  }
);

Object.defineProperty(
  Instruction.prototype,
  'category',
  {
    get: function () {
      return this.getCategory();
    }
  }
);

Object.defineProperty(
  Instruction.prototype,
  'name',
  {
    get: function () {
      return this.getName();
    }
  }
);

Object.defineProperty(
  Instruction.prototype,
  'state',
  {
    get: function () {
      return this.mState;
    }
  }
);

Object.defineProperty(
  Instruction.prototype,
  'sequence',
  {
    get: function () {
      return this.mSequence;
    }
  }
);
